import { useState, useEffect, useCallback } from 'react';
import { Layout } from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  TrendingUp, TrendingDown, DollarSign, AlertTriangle, Target, 
  CreditCard, Users, Package, RefreshCw, ChevronRight, Minus,
  AlertCircle, BarChart3, PieChart, Lightbulb, Award, Zap,
  ArrowUpRight, ArrowDownRight, Calculator, Boxes, TreePine
} from 'lucide-react';
import { 
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, 
  ResponsiveContainer, PieChart as RePieChart, Pie, Cell, Legend,
  BarChart, Bar
} from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${API_URL}/api`;

// Função para formatar moeda brasileira
const formatarMoeda = (valor) => {
  if (!valor && valor !== 0) return 'R$ 0,00';
  return valor.toLocaleString('pt-BR', { 
    style: 'currency', 
    currency: 'BRL',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
};

// Gerar lista de meses
const generateMonths = () => {
  const months = [];
  const now = new Date();
  for (let i = 0; i < 12; i++) {
    const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const value = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
    const label = date.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });
    months.push({ value, label: label.charAt(0).toUpperCase() + label.slice(1) });
  }
  return months;
};

// Cores para gráficos
const COLORS = ['#10b981', '#f59e0b', '#ef4444', '#3b82f6', '#8b5cf6', '#ec4899'];

export default function DRE() {
  const [dre, setDre] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  
  const months = generateMonths();

  const fetchDRE = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/dre?mes=${selectedMonth}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data) {
        setDre(response.data);
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || error.message || 'Erro desconhecido';
      toast.error(`Erro ao carregar DRE: ${errorMsg}`);
      console.error('Erro DRE:', error.response?.data || error);
    } finally {
      setLoading(false);
    }
  }, [selectedMonth]);

  useEffect(() => {
    fetchDRE();
  }, [fetchDRE]);

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-muted-foreground">Carregando DRE...</p>
          </div>
        </div>
      </Layout>
    );
  }

  const isLucro = dre?.lucro_liquido >= 0;
  const margemLiquida = dre?.margens?.liquida || 0;

  // Dados para gráfico de pizza (distribuição de custos)
  const custosData = [
    { name: 'Madeira/Produtos', value: Math.abs(dre?.custos_variaveis?.madeira || 0), color: '#10b981' },
    { name: 'Outros Variáveis', value: Math.abs(dre?.custos_variaveis?.outros || 0), color: '#3b82f6' },
    { name: 'Salários', value: Math.abs(dre?.custos_fixos?.salarios || 0), color: '#f59e0b' },
    { name: 'Encargos', value: Math.abs(dre?.custos_fixos?.encargos || 0), color: '#ef4444' },
    { name: 'Custos Fixos', value: Math.abs(dre?.custos_fixos?.bills || 0), color: '#8b5cf6' },
    { name: 'Despesas Op.', value: Math.abs(dre?.despesas_operacionais || 0), color: '#ec4899' },
    { name: 'Taxas Cartão', value: Math.abs(dre?.taxas_cartao || 0), color: '#64748b' },
  ].filter(item => item.value > 0);

  // Dados para gráfico de barras (DRE visual)
  const dreBarData = [
    { name: 'Faturamento', valor: dre?.faturamento?.total || 0, fill: '#10b981' },
    { name: 'Custos Var.', valor: -(dre?.custos_variaveis?.total || 0), fill: '#ef4444' },
    { name: 'Lucro Bruto', valor: dre?.lucro_bruto || 0, fill: '#3b82f6' },
    { name: 'Custos Fixos', valor: -(dre?.custos_fixos?.total || 0), fill: '#f59e0b' },
    { name: 'Lucro Líquido', valor: dre?.lucro_liquido || 0, fill: isLucro ? '#10b981' : '#ef4444' },
  ];

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <BarChart3 className="h-7 w-7 text-emerald-600" />
              DRE - Demonstrativo de Resultado
            </h1>
            <p className="text-muted-foreground">Análise completa de lucro e performance financeira</p>
          </div>
          <div className="flex gap-2 items-center">
            <Select value={selectedMonth} onValueChange={setSelectedMonth}>
              <SelectTrigger className="w-[200px]" data-testid="month-selector">
                <SelectValue placeholder="Selecione o mês" />
              </SelectTrigger>
              <SelectContent>
                {months.map((month) => (
                  <SelectItem key={month.value} value={month.value}>
                    {month.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button variant="outline" size="icon" onClick={fetchDRE} data-testid="refresh-btn">
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Alertas Inteligentes */}
        {dre?.alertas?.length > 0 && (
          <div className="space-y-2">
            {dre.alertas.map((alerta, idx) => (
              <div
                key={idx}
                className={`flex items-center gap-3 p-4 rounded-lg border ${
                  alerta.tipo === 'erro' 
                    ? 'bg-red-50 border-red-200 text-red-800 dark:bg-red-950 dark:border-red-800 dark:text-red-200' 
                    : 'bg-amber-50 border-amber-200 text-amber-800 dark:bg-amber-950 dark:border-amber-800 dark:text-amber-200'
                }`}
                data-testid={`alerta-${idx}`}
              >
                <AlertTriangle className="h-5 w-5 flex-shrink-0" />
                <span className="font-medium">{alerta.mensagem}</span>
              </div>
            ))}
          </div>
        )}

        {/* Cards Principais - Resumo */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Faturamento */}
          <Card className="border-l-4 border-l-emerald-500" data-testid="card-faturamento">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <DollarSign className="h-4 w-4" />
                Faturamento
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-emerald-600">
                {formatarMoeda(dre?.faturamento?.total)}
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                {dre?.indicadores?.qtd_pedidos || 0} pedidos | Ticket: {formatarMoeda(dre?.indicadores?.ticket_medio)}
              </p>
            </CardContent>
          </Card>

          {/* Lucro Líquido */}
          <Card className={`border-l-4 ${isLucro ? 'border-l-emerald-500' : 'border-l-red-500'}`} data-testid="card-lucro">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                {isLucro ? <TrendingUp className="h-4 w-4" /> : <TrendingDown className="h-4 w-4" />}
                Lucro Líquido
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className={`text-2xl font-bold ${isLucro ? 'text-emerald-600' : 'text-red-600'}`}>
                {formatarMoeda(dre?.lucro_liquido)}
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Margem: <span className={margemLiquida >= 0 ? 'text-emerald-600' : 'text-red-600'}>
                  {margemLiquida?.toFixed(1)}%
                </span>
              </p>
            </CardContent>
          </Card>

          {/* Ponto de Equilíbrio */}
          <Card className="border-l-4 border-l-blue-500" data-testid="card-equilibrio">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <Target className="h-4 w-4" />
                Ponto de Equilíbrio
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-blue-600">
                {formatarMoeda(dre?.ponto_equilibrio)}
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Falta: {formatarMoeda(dre?.falta_ponto_equilibrio)}
              </p>
            </CardContent>
          </Card>

          {/* Custos Totais */}
          <Card className="border-l-4 border-l-amber-500" data-testid="card-custos">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <Calculator className="h-4 w-4" />
                Custos Totais
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-amber-600">
                {formatarMoeda((dre?.custos_variaveis?.total || 0) + (dre?.custos_fixos?.total || 0) + (dre?.despesas_operacionais || 0) + (dre?.taxas_cartao || 0))}
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Fixos: {formatarMoeda(dre?.custos_fixos?.total)} | Var: {formatarMoeda(dre?.custos_variaveis?.total)}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Card Destaque: Lucro dos Produtos Vendidos */}
        <Card className={`border-2 ${(dre?.lucro_bruto || 0) >= 0 ? 'border-emerald-300 bg-emerald-50/50 dark:bg-emerald-950/30' : 'border-red-300 bg-red-50/50 dark:bg-red-950/30'}`} data-testid="card-lucro-produtos">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Package className="h-5 w-5 text-emerald-600" />
              Lucro dos Produtos Vendidos
            </CardTitle>
            <CardDescription>
              Lucro bruto gerado apenas pelos produtos, antes dos custos fixos da empresa
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {/* Receita dos Produtos */}
              <div className="text-center p-4 bg-white dark:bg-background rounded-lg shadow-sm border">
                <p className="text-xs text-muted-foreground mb-1">Receita dos Produtos</p>
                <p className="text-xl font-bold text-emerald-600">{formatarMoeda(dre?.faturamento?.pedidos)}</p>
                <p className="text-xs text-muted-foreground mt-1">{dre?.indicadores?.qtd_pedidos || 0} pedidos</p>
              </div>
              
              {/* Custo dos Produtos */}
              <div className="text-center p-4 bg-white dark:bg-background rounded-lg shadow-sm border">
                <p className="text-xs text-muted-foreground mb-1">Custo dos Produtos</p>
                <p className="text-xl font-bold text-red-600">-{formatarMoeda(dre?.custos_variaveis?.total)}</p>
                <p className="text-xs text-muted-foreground mt-1">Madeira + insumos</p>
              </div>
              
              {/* Lucro Bruto dos Produtos */}
              <div className={`text-center p-4 rounded-lg shadow-sm border-2 ${(dre?.lucro_bruto || 0) >= 0 ? 'bg-emerald-100 dark:bg-emerald-900 border-emerald-400' : 'bg-red-100 dark:bg-red-900 border-red-400'}`}>
                <p className="text-xs text-muted-foreground mb-1">Lucro Bruto dos Produtos</p>
                <p className={`text-2xl font-bold ${(dre?.lucro_bruto || 0) >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>
                  {formatarMoeda(dre?.lucro_bruto)}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  {(dre?.lucro_bruto || 0) >= 0 ? '✓ Produtos lucrativos' : '✗ Produtos no prejuízo'}
                </p>
              </div>
              
              {/* Margem Bruta */}
              <div className="text-center p-4 bg-white dark:bg-background rounded-lg shadow-sm border">
                <p className="text-xs text-muted-foreground mb-1">Margem Bruta</p>
                <p className={`text-2xl font-bold ${(dre?.margens?.bruta || 0) >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                  {dre?.margens?.bruta?.toFixed(1)}%
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  {(dre?.margens?.bruta || 0) >= 30 ? 'Margem saudável' : (dre?.margens?.bruta || 0) >= 15 ? 'Margem regular' : 'Margem baixa'}
                </p>
              </div>
            </div>
            
            {/* Comparativo visual - Responsivo */}
            <div className="mt-4 p-4 bg-slate-100 dark:bg-slate-800 rounded-lg">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-4 text-sm">
                <div className="flex items-center gap-2">
                  <div className={`w-3 h-3 rounded-full flex-shrink-0 ${(dre?.lucro_bruto || 0) >= 0 ? 'bg-emerald-500' : 'bg-red-500'}`}></div>
                  <span className="whitespace-nowrap">Lucro Bruto (Produtos):</span>
                  <strong className={(dre?.lucro_bruto || 0) >= 0 ? 'text-emerald-600' : 'text-red-600'}>{formatarMoeda(dre?.lucro_bruto)}</strong>
                </div>
                <div className="text-muted-foreground hidden sm:block">→</div>
                <div className="flex items-center gap-2">
                  <span className="whitespace-nowrap">(-) Custos Fixos:</span>
                  <strong className="text-amber-600">{formatarMoeda(dre?.custos_fixos?.total)}</strong>
                </div>
                <div className="text-muted-foreground hidden sm:block">→</div>
                <div className="flex items-center gap-2">
                  <div className={`w-3 h-3 rounded-full flex-shrink-0 ${isLucro ? 'bg-emerald-500' : 'bg-red-500'}`}></div>
                  <span className="whitespace-nowrap">Lucro Líquido:</span>
                  <strong className={isLucro ? 'text-emerald-600' : 'text-red-600'}>{formatarMoeda(dre?.lucro_liquido)}</strong>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Cards Secundários - Indicadores */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          {/* Ticket Médio */}
          <Card className="bg-gradient-to-br from-emerald-50 to-white dark:from-emerald-950 dark:to-background">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-muted-foreground mb-1">
                <CreditCard className="h-4 w-4" />
                <span className="text-xs">Ticket Médio</span>
              </div>
              <div className="text-lg font-bold">{formatarMoeda(dre?.indicadores?.ticket_medio)}</div>
            </CardContent>
          </Card>

          {/* Margem Média Produtos */}
          <Card className="bg-gradient-to-br from-blue-50 to-white dark:from-blue-950 dark:to-background">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-muted-foreground mb-1">
                <PieChart className="h-4 w-4" />
                <span className="text-xs">Margem Média</span>
              </div>
              <div className="text-lg font-bold">{dre?.margens?.media_produtos?.toFixed(1)}%</div>
            </CardContent>
          </Card>

          {/* Total Vendido */}
          <Card className="bg-gradient-to-br from-purple-50 to-white dark:from-purple-950 dark:to-background">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-muted-foreground mb-1">
                <Package className="h-4 w-4" />
                <span className="text-xs">Produtos Vendidos</span>
              </div>
              <div className="text-lg font-bold">{dre?.indicadores?.total_produtos_vendidos || 0}</div>
            </CardContent>
          </Card>

          {/* M³ Produzido */}
          <Card className="bg-gradient-to-br from-amber-50 to-white dark:from-amber-950 dark:to-background">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-muted-foreground mb-1">
                <Boxes className="h-4 w-4" />
                <span className="text-xs">M³ Produzido</span>
              </div>
              <div className="text-lg font-bold">{dre?.indicadores?.total_m3_produzido?.toFixed(2)} m³</div>
            </CardContent>
          </Card>

          {/* Produto + Lucrativo */}
          <Card className="bg-gradient-to-br from-green-50 to-white dark:from-green-950 dark:to-background">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-muted-foreground mb-1">
                <Award className="h-4 w-4" />
                <span className="text-xs">+ Lucrativo</span>
              </div>
              <div className="text-sm font-bold truncate" title={dre?.produto_mais_lucrativo?.nome}>
                {dre?.produto_mais_lucrativo?.nome || 'N/A'}
              </div>
              <div className="text-xs text-emerald-600">{dre?.produto_mais_lucrativo?.margem?.toFixed(1)}% margem</div>
            </CardContent>
          </Card>

          {/* Produto - Lucrativo */}
          <Card className="bg-gradient-to-br from-red-50 to-white dark:from-red-950 dark:to-background">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-muted-foreground mb-1">
                <TrendingDown className="h-4 w-4" />
                <span className="text-xs">- Lucrativo</span>
              </div>
              <div className="text-sm font-bold truncate" title={dre?.produto_menos_lucrativo?.nome}>
                {dre?.produto_menos_lucrativo?.nome || 'N/A'}
              </div>
              <div className="text-xs text-red-600">{dre?.produto_menos_lucrativo?.margem?.toFixed(1)}% margem</div>
            </CardContent>
          </Card>
        </div>

        {/* Meta Visual de Faturamento */}
        <Card data-testid="card-meta">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Target className="h-5 w-5 text-blue-600" />
              Meta para Equilíbrio
            </CardTitle>
            <CardDescription>Faturamento necessário para não ter prejuízo</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex justify-between text-sm mb-2">
                <span>Faturado: <strong>{formatarMoeda(dre?.faturamento?.total)}</strong></span>
                <span>Meta: <strong>{formatarMoeda(dre?.ponto_equilibrio)}</strong></span>
              </div>
              <Progress 
                value={dre?.progresso_meta || 0} 
                className="h-4"
              />
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>{dre?.progresso_meta?.toFixed(1)}% da meta</span>
                <span className={dre?.falta_ponto_equilibrio > 0 ? 'text-amber-600' : 'text-emerald-600'}>
                  {dre?.falta_ponto_equilibrio > 0 
                    ? `Faltam ${formatarMoeda(dre?.falta_ponto_equilibrio)}`
                    : 'Meta atingida!'
                  }
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Previsão Financeira */}
        <Card className="border-2 border-dashed border-blue-200 bg-blue-50/30 dark:bg-blue-950/20" data-testid="card-previsao">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Zap className="h-5 w-5 text-blue-600" />
              Previsão do Mês
            </CardTitle>
            <CardDescription>
              Baseado no ritmo atual ({dre?.dias_passados} de {dre?.dias_no_mes} dias)
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="text-center p-4 bg-white dark:bg-background rounded-lg shadow-sm">
                <p className="text-xs text-muted-foreground mb-1">Faturamento Previsto</p>
                <p className="text-xl font-bold text-blue-600">{formatarMoeda(dre?.previsao?.faturamento)}</p>
              </div>
              <div className="text-center p-4 bg-white dark:bg-background rounded-lg shadow-sm">
                <p className="text-xs text-muted-foreground mb-1">Resultado Previsto</p>
                <p className={`text-xl font-bold ${(dre?.previsao?.lucro || 0) >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                  {formatarMoeda(dre?.previsao?.lucro)}
                </p>
              </div>
              <div className="text-center p-4 bg-white dark:bg-background rounded-lg shadow-sm">
                <p className="text-xs text-muted-foreground mb-1">Ritmo Diário</p>
                <p className="text-xl font-bold">{formatarMoeda(dre?.previsao?.ritmo_diario)}/dia</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Grid com DRE Detalhado e Gráficos */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* DRE Detalhado */}
          <Card data-testid="card-dre-detalhado">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BarChart3 className="h-5 w-5 text-emerald-600" />
                Demonstrativo Detalhado
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {/* Faturamento */}
              <div className="flex justify-between items-center py-2 border-b">
                <span className="font-semibold text-emerald-700">FATURAMENTO</span>
                <span className="font-bold text-emerald-700">{formatarMoeda(dre?.faturamento?.total)}</span>
              </div>
              <div className="flex justify-between items-center pl-4 text-sm text-muted-foreground">
                <span>Pedidos ({dre?.indicadores?.qtd_pedidos})</span>
                <span>{formatarMoeda(dre?.faturamento?.pedidos)}</span>
              </div>
              <div className="flex justify-between items-center pl-4 text-sm text-muted-foreground">
                <span>Receitas Avulsas</span>
                <span>{formatarMoeda(dre?.faturamento?.receitas_avulsas)}</span>
              </div>

              {/* Custos Variáveis */}
              <div className="flex justify-between items-center py-2 border-b">
                <span className="font-semibold text-red-600">(-) CUSTOS VARIÁVEIS</span>
                <span className="font-bold text-red-600">-{formatarMoeda(dre?.custos_variaveis?.total)}</span>
              </div>
              <div className="flex justify-between items-center pl-4 text-sm text-muted-foreground">
                <span>Madeira/Produtos</span>
                <span>-{formatarMoeda(dre?.custos_variaveis?.madeira)}</span>
              </div>
              <div className="flex justify-between items-center pl-4 text-sm text-muted-foreground">
                <span>Outros</span>
                <span>-{formatarMoeda(dre?.custos_variaveis?.outros)}</span>
              </div>

              {/* Lucro Bruto */}
              <div className="flex justify-between items-center py-2 border-b bg-blue-50 dark:bg-blue-950 px-2 rounded">
                <span className="font-semibold text-blue-700">(=) LUCRO BRUTO</span>
                <span className="font-bold text-blue-700">{formatarMoeda(dre?.lucro_bruto)}</span>
              </div>
              <div className="flex justify-between items-center pl-4 text-sm text-muted-foreground">
                <span>Margem Bruta</span>
                <span>{dre?.margens?.bruta?.toFixed(1)}%</span>
              </div>

              {/* Custos Fixos */}
              <div className="flex justify-between items-center py-2 border-b">
                <span className="font-semibold text-amber-600">(-) CUSTOS FIXOS</span>
                <span className="font-bold text-amber-600">-{formatarMoeda(dre?.custos_fixos?.total)}</span>
              </div>
              <div className="flex justify-between items-center pl-4 text-sm text-muted-foreground">
                <span>Salários</span>
                <span>-{formatarMoeda(dre?.custos_fixos?.salarios)}</span>
              </div>
              <div className="flex justify-between items-center pl-4 text-sm text-muted-foreground">
                <span>Encargos (40%)</span>
                <span>-{formatarMoeda(dre?.custos_fixos?.encargos)}</span>
              </div>
              <div className="flex justify-between items-center pl-4 text-sm text-muted-foreground">
                <span>Outros Fixos</span>
                <span>-{formatarMoeda(dre?.custos_fixos?.bills)}</span>
              </div>

              {/* Despesas e Taxas */}
              <div className="flex justify-between items-center py-2 border-b">
                <span className="font-semibold text-purple-600">(-) DESPESAS OPERACIONAIS</span>
                <span className="font-bold text-purple-600">-{formatarMoeda(dre?.despesas_operacionais)}</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b">
                <span className="font-semibold text-slate-600">(-) TAXAS CARTÃO</span>
                <span className="font-bold text-slate-600">-{formatarMoeda(dre?.taxas_cartao)}</span>
              </div>

              {/* Lucro Líquido */}
              <div className={`flex justify-between items-center py-3 px-2 rounded-lg ${isLucro ? 'bg-emerald-100 dark:bg-emerald-950' : 'bg-red-100 dark:bg-red-950'}`}>
                <span className={`font-bold ${isLucro ? 'text-emerald-800' : 'text-red-800'}`}>(=) LUCRO LÍQUIDO</span>
                <span className={`text-xl font-bold ${isLucro ? 'text-emerald-800' : 'text-red-800'}`}>
                  {formatarMoeda(dre?.lucro_liquido)}
                </span>
              </div>
              <div className="flex justify-between items-center pl-4 text-sm">
                <span className="text-muted-foreground">Margem Líquida</span>
                <span className={margemLiquida >= 0 ? 'text-emerald-600 font-semibold' : 'text-red-600 font-semibold'}>
                  {margemLiquida?.toFixed(1)}%
                </span>
              </div>
            </CardContent>
          </Card>

          {/* Gráfico de Distribuição de Custos */}
          <Card data-testid="card-grafico-custos">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <PieChart className="h-5 w-5 text-amber-600" />
                Distribuição de Custos
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-[350px]">
                <ResponsiveContainer width="100%" height="100%">
                  <RePieChart>
                    <Pie
                      data={custosData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      outerRadius={120}
                      fill="#8884d8"
                      dataKey="value"
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    >
                      {custosData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value) => formatarMoeda(value)} />
                  </RePieChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Gráfico de Barras do DRE */}
        <Card data-testid="card-grafico-dre">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-blue-600" />
              Visão Geral do DRE
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={dreBarData} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" tickFormatter={(v) => formatarMoeda(v)} />
                  <YAxis dataKey="name" type="category" width={100} />
                  <Tooltip formatter={(value) => formatarMoeda(value)} />
                  <Bar dataKey="valor" radius={[0, 4, 4, 0]}>
                    {dreBarData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.fill} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Análise Inteligente */}
        {dre?.analises?.length > 0 && (
          <Card className="border-2 border-amber-200 bg-amber-50/30 dark:bg-amber-950/20" data-testid="card-analise">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Lightbulb className="h-5 w-5 text-amber-600" />
                Análise Inteligente
              </CardTitle>
              <CardDescription>Observações automáticas baseadas nos números</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid gap-3">
                {dre.analises.map((analise, idx) => (
                  <div 
                    key={idx}
                    className={`flex items-start gap-3 p-3 rounded-lg ${
                      analise.tipo === 'alerta' ? 'bg-red-100 dark:bg-red-950' :
                      analise.tipo === 'atencao' ? 'bg-amber-100 dark:bg-amber-950' :
                      'bg-blue-100 dark:bg-blue-950'
                    }`}
                  >
                    {analise.tipo === 'alerta' ? (
                      <AlertTriangle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                    ) : analise.tipo === 'atencao' ? (
                      <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    ) : (
                      <Lightbulb className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    )}
                    <span className="text-sm">{analise.texto}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Ranking de Produtos */}
        {dre?.produtos_ranking?.length > 0 && (
          <Card data-testid="card-ranking">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Award className="h-5 w-5 text-emerald-600" />
                Ranking de Produtos por Lucro
              </CardTitle>
              <CardDescription>Top 10 produtos mais lucrativos do mês</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-2 px-2">#</th>
                      <th className="text-left py-2 px-2">Produto</th>
                      <th className="text-right py-2 px-2">Qtd</th>
                      <th className="text-right py-2 px-2">Receita</th>
                      <th className="text-right py-2 px-2">Custo</th>
                      <th className="text-right py-2 px-2">Lucro</th>
                      <th className="text-right py-2 px-2">Margem</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dre.produtos_ranking.map((prod, idx) => (
                      <tr key={idx} className="border-b hover:bg-muted/50">
                        <td className="py-2 px-2">
                          <Badge variant={idx < 3 ? 'default' : 'secondary'}>
                            {idx + 1}º
                          </Badge>
                        </td>
                        <td className="py-2 px-2 font-medium">{prod.nome}</td>
                        <td className="py-2 px-2 text-right">{prod.quantidade}</td>
                        <td className="py-2 px-2 text-right text-emerald-600">{formatarMoeda(prod.receita)}</td>
                        <td className="py-2 px-2 text-right text-red-600">{formatarMoeda(prod.custo)}</td>
                        <td className={`py-2 px-2 text-right font-semibold ${prod.lucro >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                          {formatarMoeda(prod.lucro)}
                        </td>
                        <td className="py-2 px-2 text-right">
                          <Badge variant={prod.margem >= 30 ? 'default' : prod.margem >= 15 ? 'secondary' : 'destructive'}>
                            {prod.margem?.toFixed(1)}%
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
}
